import { create } from 'zustand';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { COLLECTIONS } from '../services/firestore';

const normalizeTool = (snapshot) => ({
  id: snapshot.id,
  ...snapshot.data(),
  createdAt: snapshot.data().createdAt?.toDate() || new Date(),
  updatedAt: snapshot.data().updatedAt?.toDate() || new Date(),
});

export const useToolsStore = create((set) => ({
  gems: [],
  sharedGem: null,
  isLoading: false,
  error: null,

  loadGems: async (userId) => {
    try {
      set({ isLoading: true, error: null });

      const gemsQuery = query(
        collection(db, COLLECTIONS.TOOLS),
        where('userId', '==', userId)
      );

      const snapshot = await getDocs(gemsQuery);
      const gems = snapshot.docs
        .map(normalizeTool)
        .filter((tool) => tool.type === 'gemini-gem')
        .sort((a, b) => b.createdAt - a.createdAt);
      set({ gems, isLoading: false });
      return gems;
    } catch (error) {
      console.error('Error loading tools:', error);
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  createGem: async (gemData) => {
    try {
      set({ error: null });

      const docRef = await addDoc(collection(db, COLLECTIONS.TOOLS), {
        ...gemData,
        type: 'gemini-gem',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const createdGem = {
        id: docRef.id,
        ...gemData,
        type: 'gemini-gem',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      set((state) => ({ gems: [createdGem, ...state.gems] }));
      return createdGem;
    } catch (error) {
      console.error('Error creating tool:', error);
      set({ error: error.message });
      throw error;
    }
  },

  updateGem: async (gemId, updates) => {
    try {
      set({ error: null });
      await updateDoc(doc(db, COLLECTIONS.TOOLS, gemId), {
        ...updates,
        updatedAt: serverTimestamp(),
      });

      set((state) => ({
        gems: state.gems.map((gem) =>
          gem.id === gemId
            ? {
                ...gem,
                ...updates,
                updatedAt: new Date(),
              }
            : gem
        ),
      }));
    } catch (error) {
      console.error('Error updating tool:', error);
      set({ error: error.message });
      throw error;
    }
  },

  deleteGem: async (gemId) => {
    try {
      set({ error: null });
      await deleteDoc(doc(db, COLLECTIONS.TOOLS, gemId));
      set((state) => ({
        gems: state.gems.filter((gem) => gem.id !== gemId),
      }));
    } catch (error) {
      console.error('Error deleting tool:', error);
      set({ error: error.message });
      throw error;
    }
  },

  loadPublicGemById: async (gemId) => {
    try {
      set({ isLoading: true, error: null, sharedGem: null });

      const snapshot = await getDoc(doc(db, COLLECTIONS.TOOLS, gemId));
      if (!snapshot.exists()) {
        const error = new Error('This shared AI tool could not be found.');
        error.code = 'not-found';
        throw error;
      }

      const gem = normalizeTool(snapshot);
      if (gem.type !== 'gemini-gem' || gem.isPublic !== true) {
        const error = new Error('This AI tool is not available publicly.');
        error.code = 'permission-denied';
        throw error;
      }

      set({ sharedGem: gem, isLoading: false });
      return gem;
    } catch (error) {
      console.error('Error loading shared tool:', error);
      set({ error: error.message, isLoading: false, sharedGem: null });
      throw error;
    }
  },

  clearSharedGem: () => set({ sharedGem: null }),
  clearToolsError: () => set({ error: null }),
}));
